import { MAIN_MEDIA_STREAM_ID } from '@anm/constants/streaming';
import videoConstrains, { VideoConstrainOptions } from '@anm/data/media/videoConstrains';
import { Logger } from '@anm/helpers/Debugger';
import getUserMedia from '@anm/helpers/media/getUserMedia';
import mediaManager from '@anm/helpers/media/mediaManager';

import { getConstraints } from '../getConstraints';

type CreateStreamSettings = { echoCancellation?: boolean; resolution?: VideoConstrainOptions };
export type CreateStreamProps = {
  micId?: string;
  webcamId?: string;
  streamName?: string;
  settings?: CreateStreamSettings;
};

const errorList = ['NotAllowedError', 'NotFoundError', 'NotReadableError'] as const;

export type MediaErrorName = typeof errorList[number];
export const isStreamError = (error: string = '', exclude: MediaErrorName[] = []): error is MediaErrorName =>
  !!error && errorList.filter(v => !exclude.includes(v)).includes(error as MediaErrorName);

const logger = new Logger('mediastreams');

const createConstrainsForCurrentStream = (settings: CreateStreamSettings, micId?: string, webcamId?: string) => {
  const resolutionName = mediaManager().webcams.getCurrentOptions().resolution;
  const defaultEchoCancellation = mediaManager().mics.getCurrentOptions().echoCancellation;
  const { echoCancellation = defaultEchoCancellation, resolution = videoConstrains(resolutionName) } = settings;

  const constraints = {
    ...(micId && getConstraints(micId, { ...mediaManager().mics.getCurrentOptions(), echoCancellation })),
    ...(webcamId && {
      video: {
        deviceId: { exact: webcamId },
        frameRate: { ideal: 30 },
        ...resolution
      }
    })
  } as MediaStreamConstraints;

  return constraints;
};

const createStream = async ({
  micId,
  webcamId,
  settings = {},
  streamName = MAIN_MEDIA_STREAM_ID
}: CreateStreamProps) => {
  const timeout = 10_000;
  const delayStep = 1_000;
  let currentDelay = 0;

  try {
    const waitStream = async (resolve: (stream: MediaStream) => void, reject: (msg?: string) => void) => {
      currentDelay = currentDelay + delayStep;
      if (currentDelay >= timeout) {
        reject(`Did not wait for stream creation by webcamId:${webcamId} and micId: ${micId}`);
        return;
      }
      const constraints = createConstrainsForCurrentStream(settings, micId, webcamId);
      try {
        const stream = await getUserMedia(constraints, streamName);
        stream
          ? resolve(stream)
          : setTimeout(() => {
              waitStream(resolve, reject);
            }, delayStep);
      } catch (e) {
        logger.warn(`error getting stream [${streamName}]: ${JSON.stringify(constraints)}`, e);
        setTimeout(() => waitStream(resolve, reject), delayStep);
      }
    };

    const stream = await Promise.race([
      new Promise<MediaStream>(waitStream),
      new Promise<null>((_, reject) =>
        setTimeout(() => {
          reject(null);
        }, timeout)
      )
    ]);

    mediaManager().refresh(false);
    return stream;
  } catch (e) {
    const err = e as any;
    logger.warn(err, createConstrainsForCurrentStream(settings, micId, webcamId));
    const name = (err as Error)?.name as MediaErrorName | undefined;
    return isStreamError(name) ? Promise.reject(name) : null;
  }
};

export default createStream;
